import React, { useState } from 'react';
import { Mail, Clock, Users, Trophy, Gamepad2, Eye, Calendar, CheckCircle, MapPin, Phone, CreditCard } from 'lucide-react';

const OGGameStopPackages = () => {
  const [selectedPackage, setSelectedPackage] = useState('');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    paymentMethod: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const packages = [
    {
      id: 'kids-weekly',
      name: 'Kids Weekly',
      duration: '7 days',
      hours: '5 hours + 1 hour FREE (11 sessions total)',
      price: 80,
      savings: 20,
      discount: '20% off + 1 FREE hour',
      target: 'Parents/Children',
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      id: 'student-monthly',
      name: 'Student Monthly',
      duration: '30 days',
      hours: '15 hours + 3 hours FREE (36 sessions total)',
      price: 250,
      savings: 50,
      discount: '17% off + 3 FREE hours',
      target: 'Students/Teens',
      icon: Gamepad2,
      color: 'bg-green-500'
    },
    {
      id: 'gaming-enthusiast',
      name: 'Gaming Enthusiast',
      duration: '30 days',
      hours: '25 hours + 5 hours FREE (60 sessions total)',
      price: 400,
      savings: 100,
      discount: '20% off + 5 FREE hours',
      target: 'Serious Gamers',
      icon: Trophy,
      color: 'bg-purple-500'
    },
    {
      id: 'vr-experience',
      name: 'VR Experience',
      duration: '30 days',
      hours: '10 hours (20 sessions) - EC$15 per session',
      price: 160,
      savings: 40,
      discount: '20% off VR sessions',
      target: 'VR Enthusiasts',
      icon: Eye,
      color: 'bg-orange-500'
    },
    {
      id: 'vr-racing-package',
      name: 'VR Racing Package',
      duration: '30 days',
      hours: '15 hours VR Racing (30 sessions) - EC$15 per session',
      price: 240,
      savings: 60,
      discount: '20% off VR Racing',
      target: 'Racing Enthusiasts',
      icon: Trophy,
      color: 'bg-red-500'
    },
    {
      id: 'family-package',
      name: 'Family Package',
      duration: '30 days',
      hours: '20 hours (40 sessions - shareable)',
      price: 320,
      savings: 80,
      discount: '20% off',
      target: 'Families',
      icon: Users,
      color: 'bg-pink-500'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!selectedPackage || !customerInfo.name || !customerInfo.email || !customerInfo.phone || !customerInfo.paymentMethod) {
      alert('Please fill in all required fields including payment method');
      return;
    }

    const selectedPackageInfo = packages.find(pkg => pkg.id === selectedPackage);
    
    // Prepare email data
    const emailData = {
      to: 'browneinc7@gmail.com',
      subject: `New Gaming Package Request - ${selectedPackageInfo.name}`,
      html: `
        <h2>New Gaming Package Request</h2>
        <h3>Package Details:</h3>
        <ul>
          <li><strong>Package:</strong> ${selectedPackageInfo.name}</li>
          <li><strong>Duration:</strong> ${selectedPackageInfo.duration}</li>
          <li><strong>Gaming Hours:</strong> ${selectedPackageInfo.hours}</li>
          <li><strong>Price:</strong> EC$${selectedPackageInfo.price}</li>
          <li><strong>Savings:</strong> EC$${selectedPackageInfo.savings} (${selectedPackageInfo.discount})</li>
        </ul>
        
        <h3>Customer Information:</h3>
        <ul>
          <li><strong>Name:</strong> ${customerInfo.name}</li>
          <li><strong>Email:</strong> ${customerInfo.email}</li>
          <li><strong>Phone:</strong> ${customerInfo.phone}</li>
          <li><strong>Payment Method:</strong> ${customerInfo.paymentMethod}</li>
          <li><strong>Notes:</strong> ${customerInfo.notes || 'None'}</li>
        </ul>
        
        <p><strong>Request Time:</strong> ${new Date().toLocaleString()}</p>
        
        <p>Please contact the customer to provide banking information for ${customerInfo.paymentMethod} payment.</p>
      `
    };

    // Send email using EmailJS or similar service
    try {
      // For now, we'll use a simple mailto link
      const mailtoLink = `mailto:browneinc7@gmail.com?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent(`
New Gaming Package Request

Package Details:
- Package: ${selectedPackageInfo.name}
- Duration: ${selectedPackageInfo.duration}
- Gaming Hours: ${selectedPackageInfo.hours}
- Price: EC$${selectedPackageInfo.price}
- Savings: EC$${selectedPackageInfo.savings} (${selectedPackageInfo.discount})

Customer Information:
- Name: ${customerInfo.name}
- Email: ${customerInfo.email}
- Phone: ${customerInfo.phone}
- Payment Method: ${customerInfo.paymentMethod}
- Notes: ${customerInfo.notes || 'None'}

Request Time: ${new Date().toLocaleString()}

Please contact the customer to provide banking information for ${customerInfo.paymentMethod} payment.
      `)}`;
      
      window.open(mailtoLink);
      
      console.log('Package Request:', {
        package: selectedPackageInfo,
        customer: customerInfo,
        timestamp: new Date().toISOString()
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error('Error sending email:', error);
      alert('There was an error sending your request. Please try again or call us directly at (869) 660-1052');
    }
  };

  if (isSubmitted) {
    const selectedPackageInfo = packages.find(pkg => pkg.id === selectedPackage);

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 p-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-xl p-8 text-center">
            <div className="mb-6">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Request Submitted!</h2>
              <p className="text-gray-600">Your package request has been sent to OG's Game Stop.</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 text-left mb-6">
              <h3 className="font-bold text-lg mb-4 text-center text-purple-700">Request Summary</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium">Package:</span>
                  <span className="text-purple-600 font-semibold">{selectedPackageInfo.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Duration:</span>
                  <span>{selectedPackageInfo.duration}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Gaming Hours:</span>
                  <span>{selectedPackageInfo.hours}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Price:</span>
                  <span className="text-green-600 font-bold">EC${selectedPackageInfo.price}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Your Savings:</span>
                  <span className="text-green-600 font-bold">EC${selectedPackageInfo.savings} ({selectedPackageInfo.discount})</span>
                </div>
                <hr className="my-3" />
                <div className="flex justify-between">
                  <span className="font-medium">Customer:</span>
                  <span>{customerInfo.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Email:</span>
                  <span className="text-blue-600">{customerInfo.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Phone:</span>
                  <span className="text-blue-600">{customerInfo.phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Payment Method:</span>
                  <span className="text-green-600 font-semibold">{customerInfo.paymentMethod}</span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-6">
              <div className="text-left">
                <h4 className="font-semibold text-blue-800 mb-2">What's Next?</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• OG's Game Stop will contact you within 24 hours</li>
                  <li>• You'll receive {customerInfo.paymentMethod} banking details via text message</li>
                  <li>• Complete payment and your gaming package will be activated</li>
                  <li>• Visit us at Main Street, Charlestown, Nevis to start gaming!</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => {
                setIsSubmitted(false);
                setSelectedPackage('');
                setCustomerInfo({ name: '', email: '', phone: '', notes: '', paymentMethod: '' });
              }}
              className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-colors font-semibold"
            >
              Request Another Package
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Hero Header */}
        <div className="relative overflow-hidden rounded-2xl mb-10 shadow-xl">
          <div
            className="relative z-10 px-6 sm:px-10 py-14 sm:py-20 text-center"
            style={{
              background:
                'linear-gradient(180deg, rgba(7,11,40,0.85) 0%, rgba(21,18,61,0.85) 60%, rgba(56,25,120,0.85) 100%)'
            }}
          >
            {/* PlayStation Mark */}
            <div className="mx-auto mb-6 flex items-center justify-center gap-3 opacity-95">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.1 2.2c.6.2 1 .7 1 1.3v15.8l-2.8-.9V5.1c0-.9.9-1.6 1.8-1.3z" fill="#2E7CF6"/>
                <path d="M21.6 16.9c0 .9-.6 1.7-1.4 2l-8.9 3.1v-2.6l8-2.8c.4-.1.7-.5.7-.9 0-.4-.3-.7-.7-.9l-8-2.8V9.4l8.9 3.1c.8.3 1.4 1.1 1.4 2v2.4z" fill="#4E9CF8"/>
                <path d="M6.6 14.4c2.6.9 3.7 2.3 3.7 3.8 0 2.6-2.2 3.4-5.3 2.3-2.8-.9-3.9-2.3-3.9-3.9 0-2.6 2.2-3.3 5.5-2.2zm-.5 4.8c1.4.5 2.6.2 2.6-.8 0-.8-.7-1.5-2.2-1.9-1.6-.6-2.6-.3-2.6.7 0 .8.7 1.5 2.2 2z" fill="#7AB2FF"/>
              </svg>
              <span className="text-blue-200 uppercase tracking-widest text-sm font-semibold">PlayStation</span>
            </div>

            <h1 className="text-4xl sm:text-6xl font-extrabold leading-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-300 via-white to-purple-200 drop-shadow">
              OG's Game Stop
            </h1>
            <p className="mt-3 text-lg sm:text-2xl text-blue-100/90">Premium Gaming Experience Center</p>
            <p className="mt-1 text-blue-100/80">Choose your gaming package and jump in.</p>

          <div className="mt-6 inline-flex gap-4">
            <a href="#packages" className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold shadow-lg hover:from-blue-600 hover:to-purple-700 transition-colors">View Packages</a>
            <a href="#contact" className="px-6 py-3 rounded-xl bg-white/10 text-white font-semibold backdrop-blur hover:bg-white/20 transition-colors">Contact</a>
          </div>
          </div>

          {/* PS5 Wallpaper Background */}
          <div
            className="absolute inset-0 -z-0"
            style={{
              backgroundImage: 'url(/gaming-setup.webp)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(2px) brightness(0.7)'
            }}
          />
        </div>

        {/* Removed top Visit/Call banner (moved to Contact) */}


        {/* Gaming Equipment Showcase */}
        <div id="gallery" className="bg-white rounded-xl shadow-xl p-8 mb-8 relative overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-200 rounded-full opacity-20 animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-200 rounded-full opacity-20 animate-bounce"></div>
          
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-800 relative z-10">Our Premium Gaming Equipment</h2>
          
          {/* Main Equipment Images */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 relative z-10">
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 border-2 border-red-200 hover:shadow-xl transition-all duration-300">
              <img 
                src="/ps5-vr-setup-2.jpg" 
                alt="Complete Gaming Setup with PS5, Racing Wheel, VR2, and Playseat"
                className="w-full h-64 object-cover rounded-lg mb-4 shadow-lg"
              />
              <h3 className="font-bold text-xl text-red-700 mb-2">Complete Gaming Experience</h3>
              <p className="text-gray-600">PS5 Console, Logitech G Racing Wheel, PlayStation VR2, and Playseat Racing Cockpit</p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200 hover:shadow-xl transition-all duration-300">
              <img 
                src="/racing-simulator.jpg" 
                alt="Red Bull Racing Simulator with Playseat and Fanatec Wheel"
                className="w-full h-64 object-cover rounded-lg mb-4 shadow-lg"
              />
              <h3 className="font-bold text-xl text-blue-700 mb-2">Red Bull Racing Simulator</h3>
              <p className="text-gray-600">Official Red Bull Racing Playseat with Fanatec Direct Drive Wheel and Premium Pedals</p>
            </div>
          </div>

          {/* Additional Equipment Images */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 relative z-10">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200 hover:shadow-xl transition-all duration-300">
              <img 
                src="/playseat-f1.webp" 
                alt="Playseat F1 Racing Cockpit"
                className="w-full h-64 object-cover rounded-lg mb-4 shadow-lg"
              />
              <h3 className="font-bold text-xl text-green-700 mb-2">Playseat F1 Racing Cockpit</h3>
              <p className="text-gray-600">Professional F1-style racing seat with premium build quality and authentic racing position</p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200 hover:shadow-xl transition-all duration-300">
              <img 
                src="/gaming-setup.webp" 
                alt="PlayStation VR2 Setup"
                className="w-full h-64 object-cover rounded-lg mb-4 shadow-lg"
              />
              <h3 className="font-bold text-xl text-purple-700 mb-2">PlayStation VR2 Experience</h3>
              <p className="text-gray-600">Next-generation VR gaming with PlayStation VR2 headset and Sense controllers</p>
            </div>
          </div>

          {/* Equipment Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            <div className="text-center p-4 bg-gradient-to-br from-red-50 to-orange-50 rounded-lg border-2 border-red-200 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-lg text-red-700 mb-2">VR Racing Stations</h3>
              <p className="text-sm text-gray-600">PS5 + PS VR2 + Playseat F1 Racing Chairs</p>
            </div>
            
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                <Gamepad2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-lg text-blue-700 mb-2">Regular Gaming Stations</h3>
              <p className="text-sm text-gray-600">PS5 console stations with premium TVs</p>
            </div>
            
            <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border-2 border-green-200 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-lg text-green-700 mb-2">Premium Racing Seats</h3>
              <p className="text-sm text-gray-600">Playseat F1 and Red Bull Racing seats</p>
            </div>
            
            <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                <Trophy className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-lg text-purple-700 mb-2">Tournament Hosting</h3>
              <p className="text-sm text-gray-600">PUBG, FIFA, Call of Duty competitions</p>
            </div>
          </div>
        </div>

        {/* Popular Games Section */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-xl shadow-xl p-8 mb-8 text-white relative overflow-hidden">
          {/* Floating game icons animation */}
          <div className="absolute top-4 left-4 w-8 h-8 bg-white/20 rounded opacity-50 animate-bounce" style={{animationDelay: '0s'}}></div>
          <div className="absolute top-8 right-8 w-6 h-6 bg-white/20 rounded opacity-50 animate-bounce" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-8 left-16 w-4 h-4 bg-white/20 rounded opacity-50 animate-bounce" style={{animationDelay: '2s'}}></div>
          
          <div className="text-center relative z-10">
            <Gamepad2 className="w-12 h-12 mx-auto mb-4 animate-pulse" />
            <h2 className="text-3xl font-bold mb-6">Popular Games Available</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
              <div className="bg-white/20 backdrop-blur rounded-lg p-4 hover:bg-white/30 transition-all duration-300">
                <h3 className="font-bold text-lg mb-2">🏆 Tournament Games</h3>
                <p className="text-sm">PUBG • FIFA • Call of Duty • Apex Legends • Fortnite • Rocket League • Street Fighter 6</p>
              </div>
              
              <div className="bg-white/20 backdrop-blur rounded-lg p-4 hover:bg-white/30 transition-all duration-300">
                <h3 className="font-bold text-lg mb-2">🎮 Popular Titles</h3>
                <p className="text-sm">Grand Theft Auto • Spider-Man • God of War • Assassin's Creed • Minecraft • Monster Hunter</p>
              </div>
              
              <div className="bg-white/20 backdrop-blur rounded-lg p-4 hover:bg-white/30 transition-all duration-300">
                <h3 className="font-bold text-lg mb-2">👶 Kids Games</h3>
                <p className="text-sm">Roblox • Minecraft • Fall Guys • Paw Patrol • Mario Kart • Super Mario • Stardew Valley</p>
              </div>
              
              <div className="bg-white/20 backdrop-blur rounded-lg p-4 hover:bg-white/30 transition-all duration-300">
                <h3 className="font-bold text-lg mb-2">🏎️ VR Racing</h3>
                <p className="text-sm">F1 Racing • Gran Turismo • Need for Speed • Dirt Rally • Assetto Corsa • Project Cars</p>
              </div>
              
              <div className="bg-white/20 backdrop-blur rounded-lg p-4 hover:bg-white/30 transition-all duration-300">
                <h3 className="font-bold text-lg mb-2">🥽 VR Experience</h3>
                <p className="text-sm">Beat Saber • Half-Life Alyx • Superhot VR • Rec Room • VRChat • Job Simulator</p>
              </div>
            </div>
          </div>
        </div>

        {/* Kids Safe Zone Section */}
        <div className="bg-gradient-to-r from-green-400 via-blue-400 to-purple-400 rounded-xl shadow-xl p-8 mb-8 text-white relative overflow-hidden">
          {/* Animated elements */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-yellow-300/20 rounded-full animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-pink-300/20 rounded-full animate-bounce"></div>
          
          <div className="text-center relative z-10">
            <div className="flex justify-center items-center mb-4">
              <Users className="w-8 h-8 mr-2" />
              <span className="text-2xl">👨‍👩‍👧‍👦</span>
            </div>
            <h2 className="text-3xl font-bold mb-4">Safe Space for Kids</h2>
            <p className="text-lg mb-6 max-w-4xl mx-auto">
              Parents, feel free to run errands while your kids enjoy safe, supervised gaming! 
              We provide a secure environment where children can play age-appropriate games like 
              Roblox, Minecraft, and other kid-friendly titles while you take care of your tasks.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                <div className="text-2xl mb-2">🔒</div>
                <h3 className="font-bold mb-1">Safe Environment</h3>
                <p className="text-sm opacity-90">Supervised gaming area with staff oversight</p>
              </div>
              
              <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                <div className="text-2xl mb-2">🎯</div>
                <h3 className="font-bold mb-1">Age-Appropriate Games</h3>
                <p className="text-sm opacity-90">Curated selection of kid-friendly content</p>
              </div>
              
              <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                <div className="text-2xl mb-2">⏰</div>
                <h3 className="font-bold mb-1">Flexible Drop-Off</h3>
                <p className="text-sm opacity-90">Perfect while you run errands or appointments</p>
              </div>
            </div>
          </div>
        </div>

        {/* Coming Soon - Video Section */}
        <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-xl shadow-xl p-8 mb-8 text-white relative overflow-hidden">
          {/* Animated elements */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-yellow-300/20 rounded-full animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-white/10 rounded-full animate-bounce"></div>
          
          <div className="text-center relative z-10">
            <div className="flex justify-center items-center mb-4">
              <Eye className="w-8 h-8 mr-2" />
              <span className="text-2xl">🎬</span>
            </div>
            <h2 className="text-3xl font-bold mb-4">Coming Soon</h2>
            <p className="text-lg mb-6 max-w-4xl mx-auto">
              Get ready for the ultimate gaming experience! Watch this preview of what's coming to OG's Game Stop - 
              premium VR racing, tournament competitions, and state-of-the-art gaming setups.
            </p>
            
            <div className="bg-white/20 backdrop-blur rounded-lg p-6 max-w-4xl mx-auto">
              <div className="flex items-center justify-center mb-4">
                <span className="inline-flex items-center px-4 py-2 bg-yellow-400/90 text-black rounded-full text-sm font-bold mr-3">
                  <span className="animate-pulse mr-2">🔴</span>
                  COMING SOON
                </span>
                <h3 className="font-bold text-xl">Preview: Gaming Center Experience</h3>
              </div>
              <div className="relative rounded-lg overflow-hidden shadow-2xl">
                <video 
                  className="w-full h-auto max-h-96"
                  controls
                  autoPlay
                  loop
                  muted
                  playsInline
                >
                  <source src="/racing-video.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <p className="text-sm opacity-90 mt-4">
                This is what you can expect when we launch! Red Bull Racing simulators, PlayStation VR2 experiences, 
                and premium gaming setups - all coming soon to Main Street, Charlestown, Nevis.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* Package Selection */}
          <div id="packages" className="bg-white rounded-xl shadow-xl p-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center justify-center text-gray-800">
              <Gamepad2 className="w-8 h-8 mr-3 text-purple-600" />
              Gaming Packages
            </h2>
            <p className="text-center text-gray-600 mb-8">
              All packages offer significant savings compared to regular EC$10 per 30-minute session (VR sessions are EC$15 per 30-minute session)
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {packages.map((pkg) => {
                const IconComponent = pkg.icon;
                return (
                  <div
                    key={pkg.id}
                    onClick={() => setSelectedPackage(pkg.id)}
                    className={`border-3 rounded-xl p-6 cursor-pointer transition-all duration-300 hover:shadow-xl transform hover:scale-105 ${
                      selectedPackage === pkg.id
                        ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-blue-50 shadow-lg scale-105'
                        : 'border-gray-200 hover:border-purple-300 bg-white'
                    }`}
                  >
                    <div className="text-center">
                      <div className={`w-16 h-16 ${pkg.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <div className="font-bold text-xl text-gray-800 mb-3">{pkg.name}</div>
                      
                      <div className="space-y-2 text-gray-600 mb-4">
                        <div className="flex items-center justify-center">
                          <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                          <span className="text-sm">{pkg.duration}</span>
                        </div>
                        <div className="flex items-center justify-center">
                          <Clock className="w-4 h-4 mr-2 text-green-500" />
                          <span className="text-sm">{pkg.hours}</span>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <div className="text-3xl font-bold text-green-600">EC${pkg.price}</div>
                        <div className="text-green-500 font-semibold">{pkg.discount}</div>
                        <div className="text-sm text-gray-500">Save EC${pkg.savings}</div>
                      </div>
                      
                      <div className="text-xs text-purple-600 font-medium bg-purple-100 rounded-full px-3 py-1">
                        {pkg.target}
                      </div>
                      
                      {selectedPackage === pkg.id && (
                        <div className="mt-3">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-600 text-white">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Selected
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Customer Information Form */}
          {selectedPackage && (
            <div className="bg-white rounded-xl shadow-xl p-8 animate-fadeIn">
              <h2 className="text-3xl font-bold mb-6 flex items-center justify-center text-gray-800">
                <Users className="w-8 h-8 mr-3 text-blue-600" />
                Contact Information
              </h2>
              
              <div className="max-w-2xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Package Selected
                    </label>
                    <select
                      name="package"
                      value={selectedPackage}
                      onChange={(e) => setSelectedPackage(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                    >
                      <option value="">Select Package</option>
                      {packages.map((pkg) => (
                        <option key={pkg.id} value={pkg.id}>{pkg.name} - EC${pkg.price} ({pkg.discount})</option>
                      ))}
                    </select>
                    <div className="mt-2 text-sm text-gray-600">
                      {(() => {
                        const info = packages.find(p => p.id === selectedPackage);
                        if (!info) return null;
                        return (
                          <div className="flex flex-wrap items-center gap-3">
                            <span className="font-semibold text-green-700">Price: EC${info.price}</span>
                            <span className="text-green-700">Save EC${info.savings}</span>
                            <span className="text-gray-500">{info.hours}</span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={customerInfo.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={customerInfo.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      placeholder="Enter your email"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={customerInfo.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Payment Method *
                    </label>
                    <select
                      name="paymentMethod"
                      value={customerInfo.paymentMethod}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                    >
                      <option value="">Select Payment Method</option>
                      <option value="SKN National Bank">SKN National Bank</option>
                      <option value="Bank of Nevis">Bank of Nevis</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Banking details will be sent via text message</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Special Notes (Optional)
                    </label>
                    <textarea
                      name="notes"
                      value={customerInfo.notes}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors resize-none"
                      placeholder="Any special requests or notes"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center mt-8">
                  <button
                    onClick={handleSubmit}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-12 py-4 rounded-xl text-lg font-bold hover:from-purple-700 hover:to-blue-700 transform hover:scale-105 transition-all duration-200 shadow-xl hover:shadow-2xl"
                  >
                    <Mail className="w-5 h-5 inline mr-2" />
                    Request Package
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Payment Information */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-xl p-6 mb-8 text-white">
          <div className="text-center">
            <CreditCard className="w-8 h-8 mx-auto mb-3 text-yellow-300" />
            <h2 className="text-2xl font-bold mb-4">Easy Payment Options</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                <h3 className="font-bold text-lg mb-2">🏦 SKN National Bank</h3>
                <p className="text-sm opacity-90">Mobile banking available</p>
              </div>
              <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                <h3 className="font-bold text-lg mb-2">🏛️ Bank of Nevis</h3>
                <p className="text-sm opacity-90">Mobile banking available</p>
              </div>
            </div>
            <p className="text-sm mt-4 opacity-90">
              Select your preferred bank and we'll send you the banking details via text message for easy payment!
            </p>
          </div>
        </div>

        {/* Contact & Location */}
        <div id="contact" className="bg-white rounded-xl shadow-xl p-8 mb-8">
          <h2 className="text-3xl font-bold mb-6 text-center text-gray-800">Contact & Location</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Map Embed */}
            <div className="overflow-hidden rounded-xl border-2 border-purple-200 shadow">
              <iframe
                title="OG's Game Stop Location"
                src="https://www.google.com/maps?q=Main+Street,+Charlestown,+Nevis&output=embed"
                width="100%"
                height="360"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
              <div className="p-4 bg-purple-50 border-t border-purple-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Main Street, Charlestown, Nevis
                </div>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=Main+Street,+Charlestown,+Nevis"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
                >
                  Get Directions
                </a>
              </div>
            </div>

            {/* Contact Details */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 border-2 border-purple-100">
              <h3 className="font-bold text-xl text-purple-700 mb-4">Business Hours</h3>
              <div className="space-y-2 text-gray-700 mb-6">
                <div className="flex justify-between">
                  <span className="font-medium">Open</span>
                  <span>9:00 AM – 12:00 AM</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Days</span>
                  <span>7 days a week</span>
                </div>
              </div>

              <h3 className="font-bold text-xl text-purple-700 mb-4">Contact</h3>
              <div className="space-y-2 text-gray-700">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-purple-600 mr-2" />
                  <a href="tel:+18696601052" className="text-blue-700 hover:underline">(869) 660-1052</a>
                </div>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-purple-600 mr-2" />
                  <a href="mailto:browneinc7@gmail.com" className="text-blue-700 hover:underline">browneinc7@gmail.com</a>
                </div>
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 text-purple-600 mr-2 mt-0.5" />
                  <div>
                    Main Street, Charlestown, Nevis
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-12 space-y-6">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl p-6 text-center">
            <h3 className="text-xl font-bold mb-2">How It Works</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="font-semibold">1. Choose Package</div>
                <div className="opacity-90">Select your preferred gaming package</div>
              </div>
              <div>
                <div className="font-semibold">2. Submit Request</div>
                <div className="opacity-90">Fill in your contact information</div>
              </div>
              <div>
                <div className="font-semibold">3. Get Contacted</div>
                <div className="opacity-90">We'll contact you with payment details</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl p-6">
            <h3 className="text-xl font-bold mb-4 text-center">Tournament Qualification</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold mb-2">🏆 FREE Tournament Entry</div>
                <p className="text-sm opacity-90">Complete 10+ gaming sessions with any package and enter tournaments for FREE!</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold mb-2">⚡ Walk-in Entry</div>
                <p className="text-sm opacity-90">Haven't reached 10 sessions? Pay EC$75 on tournament day to compete!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OGGameStopPackages;
